import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    try {
      const k = '__sb_test__';
      document.cookie = `${k}=1; Path=/; SameSite=Lax`;
      cache = document.cookie.includes(k);
      document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=Lax`;
    } catch {
      cache = false;
    }
    return cache ?? false;
  };
})();

const fromCookies = () => {
  try {
    if (typeof document === 'undefined') return [];
    return document.cookie.split(';').filter(Boolean).map((c) => {
      const trimmed = c.trim();
      const eqIdx = trimmed.indexOf('=');
      const name = eqIdx >= 0 ? trimmed.slice(0, eqIdx).trim() : trimmed.trim();
      const value = eqIdx >= 0 ? decodeURIComponent(trimmed.slice(eqIdx + 1)) : '';
      return { name, value };
    }).filter((c) => c.name);
  } catch {
    return [];
  }
};

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch { return []; }
};

const setCookie = (name: string, value: string, options?: any) => {
  try {
    let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=Lax`;
    if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
    if (options?.domain) s += `; Domain=${options.domain}`;
    if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
    document.cookie = s;
  } catch { /* ignore */ }
};

const deleteCookie = (name: string) => {
  try {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch { /* ignore */ }
};

let _client: ReturnType<typeof createBrowserClient> | null = null;

export function resetClient() {
  _client = null;
}

export function createClient() {
  if (_client) return _client;
  _client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      db: { schema: 'public' },
      cookies: {
        getAll: () => {
          try {
            return canUseCookies() ? fromCookies() : fromStorage();
          } catch {
            return fromStorage();
          }
        },
        setAll(cookiesToSet) {
          try {
            if (typeof document === 'undefined') return;
            if (canUseCookies()) {
              cookiesToSet.forEach(({ name, value, options }) =>
                value ? setCookie(name, value, options) : deleteCookie(name)
              );
            } else {
              cookiesToSet.forEach(({ name, value }) => {
                try {
                  value
                    ? localStorage.setItem(`${PFX}${name}`, value)
                    : localStorage.removeItem(`${PFX}${name}`);
                } catch { /* ignore */ }
              });
            }
          } catch { /* ignore */ }
        },
      },
    }
  );
  return _client;
}
